import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Meta from '../components/Meta';
import { OrganizationSchema } from '../components/StructuredData';
import ResponsiveImage from '../components/ResponsiveImage';
import useLocalePath from '../i18n/useLocalePath';
import { IOS_URL, ANDROID_URL } from '../constants/storeLinks';

export default function About() {
  const { t } = useTranslation();
  const { lp } = useLocalePath();

  const facts = t('about.facts', { returnObjects: true });

  return (
    <div className="bg-white">
      <Meta titleKey="about.meta.title" descKey="about.meta.description" />
      <OrganizationSchema />

      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <header className="text-center mb-12">
          <span className="inline-block rounded-full bg-brand/15 text-[#0a8f8f] font-rubik font-semibold text-small px-3 py-1 mb-4">
            {t('about.badge')}
          </span>
          <h1 className="font-rubik font-bold text-h2 md:text-h1 text-gray-900 mb-4">
            {t('about.heading')}
          </h1>
          <p className="font-rubik text-body text-gray-600 max-w-2xl mx-auto">
            {t('about.intro')}
          </p>
        </header>

        <section className="mb-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <ResponsiveImage
                srcBase="/images/mascot-hero"
                alt={t('about.mascotAlt')}
                className="w-40 md:w-56 drop-shadow-xl"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-rubik font-bold text-h3 text-gray-900 mb-3">
                {t('about.whatIsHeading')}
              </h2>
              <p className="font-rubik text-body text-gray-600 mb-4">
                {t('about.whatIs')}
              </p>
              <p className="font-rubik text-body text-gray-600">
                {t('about.whoBuilds')}
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-rubik font-bold text-h3 text-gray-900 mb-6">
            {t('about.factsHeading')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {facts.map((fact, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 p-6">
                <span className="text-2xl mb-2 block">{fact.icon}</span>
                <h3 className="font-rubik font-semibold text-h5 text-gray-900 mb-1">{fact.label}</h3>
                <p className="font-rubik text-bodyS text-gray-600">{fact.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-rubik font-bold text-h3 text-gray-900 mb-4">
            {t('about.diffHeading')}
          </h2>
          <ul className="space-y-3">
            {t('about.differentiators', { returnObjects: true }).map((d, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand/15 text-[#0a8f8f] flex items-center justify-center text-small font-bold mt-0.5">✓</span>
                <span className="font-rubik text-body text-gray-700">{d}</span>
              </li>
            ))}
          </ul>
        </section>

        <div className="rounded-3xl bg-gradient-to-r from-brand to-[#0fa8a8] p-8 md:p-10 text-white text-center">
          <h2 className="font-rubik font-bold text-h3 mb-4">{t('about.ctaHeading')}</h2>
          <p className="font-rubik text-body text-white/85 mb-6 max-w-lg mx-auto">{t('about.ctaBody')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={IOS_URL} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-[#0a8f8f] font-rubik font-semibold rounded-full px-6 py-3 hover:bg-gray-50 transition-colors">
              {t('about.ctaIos')}
            </a>
            <a href={ANDROID_URL} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-[#0a8f8f] font-rubik font-semibold rounded-full px-6 py-3 hover:bg-gray-50 transition-colors">
              {t('about.ctaAndroid')}
            </a>
          </div>
          <p className="font-rubik text-small text-white/65 mt-4">
            {t('about.ctaNote')}{' '}
            <Link to={lp('/faq')} className="underline text-white/80 hover:text-white">{t('about.faqLink')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
